/**
 * The kinds of record this app keeps: the template's example, Items, and
 * Majik's own (docs/majik/DATA-MODEL-V1.md §2). fields.ts and codes.ts beside
 * it describe each one's fields and codes.
 *
 * A leaf module: it imports nothing, so anything can read it without an
 * import cycle (docs/DATA-MODEL.md → "Import cycles").
 */
export const DATA_ENTITIES = [
  'item',
  'client',
  'location',
  'contact',
  'cleaner',
  'recurringJob',
  'job',
  'invoice',
  'product',
  'order',
  'inspection',
  'communication',
] as const
export type DataEntity = (typeof DATA_ENTITIES)[number]

export const ENTITY_INFO: Record<DataEntity, { label: string; description: string }> = {
  item: {
    label: 'Item',
    description:
      'One piece of work on the Projects screen — the template’s example record, which a project replaces with its own.',
  },
  client: {
    label: 'Client',
    description:
      'A billing account: one QuickBooks customer. Has one or more locations; its status is worked out from theirs.',
  },
  location: {
    label: 'Location',
    description:
      'One site cleaned for a client — an office, store, floor or apartment. Jobs, orders, recurring jobs and inspections attach here.',
  },
  contact: {
    label: 'Contact',
    description:
      'A person Majik deals with: a client’s point of contact, a billing inbox, a cleaner, an inspector or office staff. Their phones and emails are kept with them.',
  },
  cleaner: {
    label: 'Cleaner',
    description:
      'A contact who cleans for Majik, with their shift and whether they get texts. Where and when they work comes from the recurring jobs.',
  },
  recurringJob: {
    label: 'Recurring job',
    description:
      'The rule that keeps making a location’s visits: a first day, a time and how often. The one source of each cleaner’s week.',
  },
  job: {
    label: 'Job',
    description: 'One visit to one location, billed once.',
  },
  invoice: {
    label: 'Invoice',
    description:
      'A bill to a client, built here from monthly rates, completed jobs and delivered supplies, and sent through QuickBooks.',
  },
  product: {
    label: 'Product',
    description:
      'Something Majik sells or leaves at a site — a supply, a piece of equipment or a service — with its price and, for supplies, stock.',
  },
  order: {
    label: 'Order',
    description: 'A delivery of supplies to one location; its lines are billed once delivered.',
  },
  inspection: {
    label: 'Inspection',
    description:
      'One text to an inspector listing the locations to check, who cleaned each and when.',
  },
  communication: {
    label: 'Message',
    description:
      'Every text, email, note and event in a record’s Activity — linked to each record it is about.',
  },
}

/**
 * The MAIN record types: the business records the app exists to keep. Each
 * keeps its full record history and its own Created / Last updated, kept by
 * the database (`main-records.test.ts` fails until it does). Settings, users,
 * mappings and the like are not main records: the activity log covers them.
 */
export const MAIN_RECORD_TYPES: readonly DataEntity[] = DATA_ENTITIES
